import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Tab } from '../Tabs/Tabs';
import { Button } from '../Button/Button';

interface ScrollableTabItem {
  id: string;
  label: string;
  badge?: number;
}

interface ParameterEditorScrollableTabsProps {
  tabs: ScrollableTabItem[];
  activeId: string;
  onChange: (id: string) => void;
}

export const ParameterEditorScrollableTabs: React.FC<ParameterEditorScrollableTabsProps> = ({
  tabs,
  activeId,
  onChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrows();

    el.addEventListener('scroll', updateArrows, { passive: true });

    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [updateArrows, tabs]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -200 : 200,
      behavior: 'smooth',
    });
  };

  // Arrow icons as inline SVGs
  const ChevronLeft = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  );

  const ChevronRight = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );

  return (
    <div className="relative flex items-center border-b border-border-primary dark:border-dark-border-primary px-4 pt-2">
      {showLeftArrow && (
        <Button
          type="plain"
          size="sm"
          iconOnly
          leftIcon={ChevronLeft}
          onClick={() => scroll('left')}
          ariaLabel="Desplazar tabs a la izquierda"
          className="shrink-0 z-10"
        />
      )}

      <div
        ref={scrollRef}
        className="flex overflow-x-auto overflow-y-hidden scroll-smooth scrollbar-hide flex-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex items-end pb-2.5 gap-2">
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              active={tab.id === activeId}
              badge={tab.badge}
              onClick={() => onChange(tab.id)}
            />
          ))}
        </div>
      </div>

      {showRightArrow && (
        <Button
          type="plain"
          size="sm"
          iconOnly
          leftIcon={ChevronRight}
          onClick={() => scroll('right')}
          ariaLabel="Desplazar tabs a la derecha"
          className="shrink-0 z-10"
        />
      )}
    </div>
  );
};
